import React from "react";
import { TimeRange } from "modules/cards/components/RosenCardPriceChartTypes";

const TIMEFRAME_PRESETS = ["1W", "1M", "3M", "6M", "1Y"] as const;
export type TimeframePreset = (typeof TIMEFRAME_PRESETS)[number];

const PRESET_DURATIONS_MS: Record<TimeframePreset, number> = {
  "1W": 7 * 24 * 60 * 60 * 1000,
  "1M": 30 * 24 * 60 * 60 * 1000,
  "3M": 90 * 24 * 60 * 60 * 1000,
  "6M": 180 * 24 * 60 * 60 * 1000,
  "1Y": 365 * 24 * 60 * 60 * 1000,
};

/**
 * Converts a timeframe preset into a percentage-based TimeRange.
 * Always anchored to end=100 (most recent data).
 */
export function calculateTimeRange(
  preset: TimeframePreset,
  minDate: Date,
  maxDate: Date
): TimeRange {
  const totalRangeMs = maxDate.getTime() - minDate.getTime();
  if (totalRangeMs <= 0) {
    return { start: 0, end: 100 };
  }

  const durationMs = PRESET_DURATIONS_MS[preset];

  // If the data range is shorter than the selected timeframe, show all
  if (durationMs >= totalRangeMs) {
    return { start: 0, end: 100 };
  }

  const startPercent = Math.max(0, 100 - (durationMs / totalRangeMs) * 100);
  return { start: startPercent, end: 100 };
}

interface TimeframeSelectorProps {
  activeTimeframe: TimeframePreset;
  onTimeframeChange: (preset: TimeframePreset) => void;
}

export function TimeframeSelector({
  activeTimeframe,
  onTimeframeChange,
}: TimeframeSelectorProps) {
  return (
    <div className="mt-3 flex justify-center md:mt-4">
      <div className="join">
        {TIMEFRAME_PRESETS.map((preset) => (
          <button
            key={preset}
            className={`btn join-item btn-xs md:btn-sm ${
              activeTimeframe === preset ? "btn-primary" : "btn-ghost"
            }`}
            onClick={() => onTimeframeChange(preset)}
          >
            {preset}
          </button>
        ))}
      </div>
    </div>
  );
}
